import { BANQUET_OWNER_ROLES, isBanquetUserRole, isSuperAdminRole, normalizeAssignedBanquetIds } from "../model/banquetOwnerModel.js";

export const BANQUET_OWNER_COLLECTION = "banquetOwners";
export const BANQUET_COLLECTION = "banquets";
export const BANQUET_DRAFT_COLLECTION = "banquetDrafts";
export const BANQUET_MEDIA_PATH = "banquets";

export const banquetAccessRules = {
  roles: BANQUET_OWNER_ROLES,
  permissions: {
    super_admin: {
      description: "Full access to create owners, assign banquets, and manage any banquet document.",
      canCreateOwnerAccounts: true,
      canAssignBanquets: true,
      canReadAnyBanquet: true,
      canUpdateAnyBanquet: true,
      canManageOwnerProfiles: true,
    },
    banquet_owner: {
      description: "Can read and update only banquet documents explicitly assigned to their owner profile.",
      canCreateOwnerAccounts: false,
      canAssignBanquets: false,
      canReadAnyBanquet: false,
      canUpdateAnyBanquet: false,
      canManageOwnerProfiles: false,
    },
    banquet_manager: {
      description: "Can read and update assigned banquet documents and submit them for review.",
      canCreateOwnerAccounts: false,
      canAssignBanquets: false,
      canReadAnyBanquet: false,
      canUpdateAnyBanquet: false,
      canManageOwnerProfiles: false,
    },
    banquet_staff: {
      description: "Can read and update assigned banquet drafts for the launch flow.",
      canCreateOwnerAccounts: false,
      canAssignBanquets: false,
      canReadAnyBanquet: false,
      canUpdateAnyBanquet: false,
      canManageOwnerProfiles: false,
    },
  },
  collections: {
    banquetOwners: BANQUET_OWNER_COLLECTION,
    banquets: BANQUET_COLLECTION,
    banquetDrafts: BANQUET_DRAFT_COLLECTION,
  },
  storage: {
    banquetMediaPath: BANQUET_MEDIA_PATH,
  },
};

export const canManageAllBanquets = (owner) => isSuperAdminRole(owner?.role);
export const canAccessSuperAdmin = (owner) => Boolean(owner && owner.isActive !== false && isSuperAdminRole(owner.role));
export const canAccessBanquetOwnerPanel = (owner) =>
  Boolean(owner && owner.isActive !== false && (isSuperAdminRole(owner.role) || isBanquetUserRole(owner.role)));

export const canManageBanquet = (owner, banquet) => {
  if (!owner || !banquet || owner.isActive === false) {
    return false;
  }

  if (isSuperAdminRole(owner.role)) {
    return true;
  }

  if (!isBanquetUserRole(owner.role)) {
    return false;
  }

  const assignedBanquetIds = normalizeAssignedBanquetIds(owner);
  return assignedBanquetIds.includes(banquet.id) || banquet.ownerId === owner.uid;
};

export const assertCanManageBanquet = (owner, banquet) => {
  if (!canManageBanquet(owner, banquet)) {
    const error = new Error("You do not have permission to access this banquet.");
    error.code = "banquet-owner/forbidden";
    throw error;
  }
};

export const assertSuperAdminAccess = (owner) => {
  if (!canAccessSuperAdmin(owner)) {
    const error = new Error("Only a super admin can access this page.");
    error.code = "banquet-owner/super-admin-only";
    throw error;
  }
};

export const getReadableBanquetIdsForOwner = (owner) => {
  if (!owner) {
    return [];
  }

  if (isSuperAdminRole(owner.role)) {
    return ["*"];
  }

  return normalizeAssignedBanquetIds(owner);
};
